import type { ScopeRuleAxis, Scope, InventoryScopeRule } from '@/types';
import {
  activities,
  communitySubSectors,
  measureClasses,
  renderActivity,
  renderCommunitySubSector,
  renderMeasureClass,
  renderSourceType,
  sourceTypes,
} from '@/data/labels';
import { renderScope, scopes } from '@/data/scope';

// Editor support for scenario scope rules — the community analogue of
// suit's `inventory-scope-control` rule-types + enum-options, shifted to
// village's flat five-axis grain (source-type / activity / measure-class /
// sub-sector / scope). One place for: the axis registry the Add-rule modal
// renders, the value options each axis offers, the skeleton a freshly-added
// rule starts from, the diff key used to reconcile the local rules against the
// saved set, and the display label for a stored specifier. Reuses the shared
// renderers + value-lists from `data/labels` and `data/scope` (the `scope` axis
// is the SAME `Scope` token as the disclosure surface — `scope1`/`scope2`/`scope3`).

type ScopeRuleAxisDef = {
  id: ScopeRuleAxis;
  label: string;
  description: string;
  example: string;
};

// Ordered registry — coarse → specific, `scope` last as the cross-cutting axis.
const SCOPE_RULE_AXES: ScopeRuleAxisDef[] = [
  {
    id: 'source-type',
    label: 'Exclude a source type',
    description: 'Remove all activity for a source type.',
    example: 'Example: exclude Transport from a stationary-energy-only report.',
  },
  {
    id: 'activity',
    label: 'Exclude an activity',
    description: 'Remove a specific activity kind.',
    example: 'Example: exclude Private vehicle travel from this scenario.',
  },
  {
    id: 'measure-class',
    label: 'Exclude a measure class',
    description: 'Remove activities of a specific measure class.',
    example: 'Example: exclude Mains water while reporting energy only.',
  },
  {
    id: 'sub-sector',
    label: 'Exclude a sub-sector',
    description: 'Remove all activity in a GPC sub-sector.',
    example: 'Example: exclude Industrial activity reported elsewhere.',
  },
  {
    id: 'scope',
    label: 'Exclude a GHG scope',
    description: 'Remove an entire GHG scope (1, 2, or 3).',
    example: 'Example: exclude all Scope 3 from a Scope 1+2 report.',
  },
];

const scopeRuleAxisLabels: Record<ScopeRuleAxis, string> = {
  'source-type': 'Source type',
  activity: 'Activity',
  'measure-class': 'Measure class',
  'sub-sector': 'Sub-sector',
  scope: 'Scope',
};

// One selectable value option (token + display label) for an axis.
type ScopeRuleOption = { id: string; label: string };

// The values an axis offers, as { id, label } pairs (alpha by label, matching
// suit's enum-options). `scope` keeps its canonical 1 → 2 → 3 order.
const byLabel = (a: ScopeRuleOption, b: ScopeRuleOption) =>
  a.label.localeCompare(b.label);

const scopeRuleAxisOptions = (axis: ScopeRuleAxis): ScopeRuleOption[] => {
  switch (axis) {
    case 'source-type':
      return sourceTypes
        .map((v) => ({ id: v, label: renderSourceType(v) }))
        .sort(byLabel);
    case 'activity':
      return activities
        .map((v) => ({ id: v, label: renderActivity(v) }))
        .sort(byLabel);
    case 'measure-class':
      return measureClasses
        .map((v) => ({ id: v, label: renderMeasureClass(v) }))
        .sort(byLabel);
    case 'sub-sector':
      return communitySubSectors
        .map((v) => ({ id: v, label: renderCommunitySubSector(v) }))
        .sort(byLabel);
    case 'scope':
      return scopes.map((v) => ({ id: v, label: renderScope(v) }));
  }
};

// Construct a specifier from an axis + a value token (a card's Select output).
const buildScopeRule = (
  axis: ScopeRuleAxis,
  value: string,
): InventoryScopeRule => {
  switch (axis) {
    case 'source-type':
      return { type: 'source-type', sourceType: value as any };
    case 'activity':
      return { type: 'activity', activity: value as any };
    case 'measure-class':
      return { type: 'measure-class', measureClass: value as any };
    case 'sub-sector':
      return { type: 'sub-sector', subSector: value as any };
    case 'scope':
      return { type: 'scope', scope: value as Scope };
  }
};

// A freshly-added rule: the axis with its first option selected (every axis is
// an enum with a valid default, so a new rule is always complete — unlike
// suit's free-form building/region pickers).
const createSkeleton = (axis: ScopeRuleAxis): InventoryScopeRule =>
  buildScopeRule(axis, scopeRuleAxisOptions(axis)[0].id);

// The current value token of a specifier (for the card's Select + the key).
const scopeRuleValue = (specifier: InventoryScopeRule): string => {
  switch (specifier.type) {
    case 'source-type':
      return specifier.sourceType;
    case 'activity':
      return specifier.activity;
    case 'measure-class':
      return specifier.measureClass;
    case 'sub-sector':
      return specifier.subSector;
    case 'scope':
      return specifier.scope;
  }
};

// Stable identity for diffing local rules against the saved set (axis + value).
const scopeRuleKey = (specifier: InventoryScopeRule): string =>
  `${specifier.type}:${scopeRuleValue(specifier)}`;

// The display label for a stored specifier's value (the axis label comes from
// `scopeRuleAxisLabels[specifier.type]`).
const renderScopeRuleValue = (specifier: InventoryScopeRule): string => {
  switch (specifier.type) {
    case 'source-type':
      return renderSourceType(specifier.sourceType);
    case 'activity':
      return renderActivity(specifier.activity);
    case 'measure-class':
      return renderMeasureClass(specifier.measureClass);
    case 'sub-sector':
      return renderCommunitySubSector(specifier.subSector);
    case 'scope':
      return renderScope(specifier.scope);
  }
};

export {
  SCOPE_RULE_AXES,
  scopeRuleAxisLabels,
  scopeRuleAxisOptions,
  buildScopeRule,
  createSkeleton,
  scopeRuleValue,
  scopeRuleKey,
  renderScopeRuleValue,
};
export type { ScopeRuleAxisDef, ScopeRuleOption };
